import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, SettingsIcon } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  FormInput,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@/shared/forms";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";
import { BASE_UNITS } from "../api/client";
import { useIngredientQuery, useUpdateIngredient } from "../api/queries";
import {
  categoryIdFromFormValue,
  categoryIdToFormValue,
  IngredientCategoryField,
} from "../ui/IngredientCategoryField";
import {
  defaultSupplierIdFromFormValue,
  defaultSupplierIdToFormValue,
  IngredientDefaultSupplierField,
} from "../ui/IngredientDefaultSupplierField";
import { IngredientUnitFields } from "../ui/IngredientUnitFields";

const FormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ວັດຖຸດິບ"),
  baseUnit: z.enum(BASE_UNITS, {
    message: "ຕ້ອງເລືອກຫນ່ວຍພື້ນຖານ",
  }),
  receiveUnit: z.string().trim().min(1).max(16),
  receiveToBase: z.coerce.number().positive("ອັດຕາຕ້ອງຫຼາຍກວ່າ 0"),
  issueUnit: z.string().trim().min(1).max(16),
  issueToBase: z.coerce.number().positive("ອັດຕາຕ້ອງຫຼາຍກວ່າ 0"),
  categoryId: z.string(),
  defaultSupplierId: z.string(),
  isActive: z.boolean(),
});

type FormValues = z.infer<typeof FormSchema>;

const unitOptions = BASE_UNITS.map((u) => ({ value: u, label: u }));

export function IngredientEditPage() {
  const { id } = useParams({ from: "/app/inventory/ingredients/$id/edit" });
  const nav = useNavigate({ from: "/app/inventory/ingredients/$id/edit" });
  const ingredientId = id ?? "";

  const ingredient = useIngredientQuery(ingredientId);
  const updateIngredient = useUpdateIngredient(ingredientId);

  const form = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      baseUnit: "g",
      receiveUnit: "g",
      receiveToBase: 1,
      issueUnit: "g",
      issueToBase: 1,
      categoryId: "__none__",
      defaultSupplierId: "__none__",
      isActive: true,
    },
  });

  const data = ingredient.data;
  useEffect(() => {
    if (!data) return;
    form.reset({
      name: data.name,
      baseUnit: data.baseUnit as FormValues["baseUnit"],
      receiveUnit: data.receiveUnit,
      receiveToBase: data.receiveToBase,
      issueUnit: data.issueUnit,
      issueToBase: data.issueToBase,
      categoryId: categoryIdToFormValue(data.categoryId),
      defaultSupplierId: defaultSupplierIdToFormValue(data.defaultSupplierId),
      isActive: data.isActive,
    });
  }, [
    data?.id,
    data?.name,
    data?.baseUnit,
    data?.receiveUnit,
    data?.receiveToBase,
    data?.issueUnit,
    data?.issueToBase,
    data?.categoryId,
    data?.defaultSupplierId,
    data?.isActive,
    form,
  ]);

  const onSave = form.handleSubmit(async (values) => {
    try {
      await updateIngredient.mutateAsync({
        name: values.name,
        baseUnit: values.baseUnit,
        receiveUnit: values.receiveUnit,
        receiveToBase: values.receiveToBase,
        issueUnit: values.issueUnit,
        issueToBase: values.issueToBase,
        isActive: values.isActive,
        categoryId: categoryIdFromFormValue(values.categoryId),
        defaultSupplierId: defaultSupplierIdFromFormValue(
          values.defaultSupplierId,
        ),
      });
      toast.success("ແກ້ໄຂວັດຖຸດິບສຳເລັດ");
      nav({ to: "/app/inventory/ingredients" });
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? "ແກ້ໄຂວັດຖຸດິບລົ້ມເຫຼວ");
    }
  });

  if (ingredient.isLoading || !data) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ວັດຖຸດິບ",
                to: "/app/inventory/ingredients",
              },
              {
                label: "ແກ້ໄຂວັດຖຸດິບ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ວັດຖຸດິບ",
              to: "/app/inventory/ingredients",
            },
            {
              label: "ແກ້ໄຂວັດຖຸດິບ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂວັດຖຸດິບ</h2>
            <p className="text-muted-foreground">ແຜງຄວບຄຸມ / ວັດຖຸດິບ</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/inventory/ingredients" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <FormRoot<FormValues>
          methods={form}
          onSubmit={onSave}
          className="space-y-6"
        >
          <Card className="rounded-xl border bg-card">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 font-heading text-lg">
                <SettingsIcon className="size-5 text-muted-foreground" />
                ຂໍ້ມູນທົ່ວໄປ
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                ຊື່ວັດຖຸດິບ ແລະ ຫນ່ວຍພື້ນຖານ.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormInput
                name="name"
                label="ຊື່ວັດຖຸດິບ"
                placeholder="ຕົວຢ່າງ: ນົມສົດ"
                requiredMark
              />
              <FormSelect
                name="baseUnit"
                label="ຫນ່ວຍພື້ນຖານ"
                options={unitOptions}
                requiredMark
              />
              <IngredientUnitFields />
              <IngredientCategoryField
                fallbackOption={
                  data.categoryId && data.categoryName
                    ? { id: data.categoryId, label: data.categoryName }
                    : undefined
                }
              />
              <IngredientDefaultSupplierField
                fallbackOption={
                  data.defaultSupplierId && data.defaultSupplierName
                    ? {
                        id: data.defaultSupplierId,
                        label: data.defaultSupplierName,
                      }
                    : undefined
                }
              />
              <p className="text-muted-foreground text-sm">
                <Link
                  to="/app/inventory/stock-policies"
                  search={{
                    policyBranch: "__global__",
                    search: data.name,
                  }}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  ຕັ້ງເງື່ອນໄຂສຕັອກ (min/target) ທົ່ວໄປ
                </Link>
              </p>
              <div className="flex items-center justify-between rounded-[12px] border bg-muted/30 p-4">
                <div>
                  <p className="font-medium text-sm">ໃຊ້ງານ</p>
                  <p className="text-muted-foreground text-xs">
                    ວັດຖຸດິບນີ້ສາມາດໃຊ້ໃນສູດ ແລະ ສາງ
                  </p>
                </div>
                <RHF.Controller
                  name="isActive"
                  control={form.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </FormRoot>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t pt-6">
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/ingredients" })}
          >
            ຍົກເລີກການປ່ຽນແປງ
          </Button>
          <Button onClick={onSave} isLoading={updateIngredient.isPending}>
            ບັນທຶກການປ່ຽນແປງ
          </Button>
        </div>
      </Main>
    </>
  );
}
